/* eslint-disable max-lines-per-function */
/* eslint-disable functional/immutable-data */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { LatLngExpression, LatLngTuple, Map } from 'leaflet';
import * as L from 'leaflet';  // ← ADICIONAR
import drawLocales from 'leaflet-draw-locales';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState, RefObject } from 'react';
import { Circle, FeatureGroup, Polygon, Rectangle, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import type { IGeographical } from '../../interfaces';

interface DrawInMapProps {
  value: IGeographical[];
  valueDrawOpen: IGeographical[];
  onChange: (value: IGeographical[]) => void;
  limit?: number;
  featureGroupRef?: RefObject<any>;
}

export default function DrawInMap({ value, valueDrawOpen, onChange, limit, featureGroupRef }: DrawInMapProps) {
  const [valueDrawOpenMoment, setValueDrawMomentOpen] = useState(valueDrawOpen);
  const [enableDraw, setEnableDraw] = useState(limit ? value.length < limit : true);
  const valueRef = useRef(value);
  const valueDrawOpenMomentRef = useRef(valueDrawOpen);
  const lastOnChangeValueRef = useRef<string>('');
  const map = useMap();

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    valueDrawOpenMomentRef.current = valueDrawOpenMoment;
  }, [valueDrawOpenMoment]);


  drawLocales('pt');

  const handleDrawWithCreate = useCallback(
    (event: any) => {
      if (event.layer && featureGroupRef?.current) {
        featureGroupRef.current.removeLayer(event.layer);
      }

      return updateDraw({
        event: event,
        created: true,
        value: valueRef.current,
        onChange,
        valueDrawOpenMoment: valueDrawOpenMomentRef.current,
        setValueDrawMomentOpen: setValueDrawMomentOpen,
        limit,
        setEnableDraw,
        enableDraw,
        lastOnChangeValueRef,
      });
    },
    [onChange, limit, enableDraw, featureGroupRef],
  );

  const handleDrawEdit = useCallback(
    (event: any) => {
      changeZIndexOverlay({ map: map, zIndex: '500' });
      const result = updateDraw({
        event: event,
        value: valueRef.current,
        onChange,
        valueDrawOpenMoment: valueDrawOpenMomentRef.current,
        setValueDrawMomentOpen: setValueDrawMomentOpen,
        limit,
        setEnableDraw,
        enableDraw,
        lastOnChangeValueRef,
      });
      
      if (featureGroupRef) {
        setTimeout(() => renumberAreas(featureGroupRef, onChange), 50);
      }
      
      return result;
    },
    [onChange, limit, map, enableDraw, featureGroupRef],
  );

  const handleDrawWithDelete = useCallback(
    (event: any) => {
      changeZIndexOverlay({ map: map, zIndex: '500' });

      const result = updateDraw({
        event: event,
        value: valueRef.current,
        onChange,
        valueDrawOpenMoment: valueDrawOpenMomentRef.current,
        setValueDrawMomentOpen: setValueDrawMomentOpen,
        limit,
        setEnableDraw,
        enableDraw,
        lastOnChangeValueRef,
      });
      
      if (featureGroupRef) {
        setTimeout(() => renumberAreas(featureGroupRef, onChange), 50);
      }
      
      return result;
    },
    [onChange, limit, map, enableDraw, featureGroupRef],
  );

  return (
    <FeatureGroup ref={featureGroupRef}>
      <EditControl
        position="bottomleft"
        onCreated={handleDrawWithCreate}
        onDeleteStart={() => {
          changeZIndexOverlay({ map: map, zIndex: '700' })
        }}
        onDeleteStop={handleDrawWithDelete}
        onEditStop={handleDrawEdit}
        draw={{
          circle: enableDraw,
          rectangle: enableDraw,
          polygon: enableDraw,
          polyline: false,
          marker: false,
          circlemarker: false,
        }}
      />
      {valueDrawOpenMoment.map((draw: IGeographical, index) => {
        if (draw.type === 'circle' && draw?.radius != undefined && draw?.point != undefined) {
          return (
            <Circle
              key={index}
              className={draw.name}
              color={draw.color}
              center={draw.point}
              radius={draw.radius}
            />
          );
        } else if (draw.type === 'rectangle' && draw?.area != undefined) {
          const rectangleBounds: [number, number][] = draw.area[0].map(latlng => [latlng.lat, latlng.lng] as [number, number]);
          return (
            <Rectangle
              key={index}
              className={draw.name}
              color={draw.color}
              bounds={rectangleBounds as any}
              pathOptions={{
                fillColor: draw.color,
                color: draw.color,
                fillOpacity: 0.4,
              }}
            />
          );
        } else if (draw.type === 'polygon' && draw?.area != undefined) {
          const polygonPositions = draw.area[0].map(latlng => [latlng.lat, latlng.lng] as LatLngExpression);
          return (
            <Polygon
              key={index}
              className={draw.name}
              color={draw.color}
              positions={polygonPositions as unknown as LatLngExpression[]}
              pathOptions={{
                fillColor: draw.color,
                color: draw.color,
                fillOpacity: 0.4,
              }}
            />
          );
        } else {
          return null;
        }
      })}
    </FeatureGroup>
  );
}

function renumberAreas(
  featureGroupRef: RefObject<any>,
  onChange: (value: IGeographical[]) => void
) {
  if (!featureGroupRef?.current) return;
  
  const areas: IGeographical[] = [];
  let layerIndex = 0;
  
  featureGroupRef.current.eachLayer((layer: any) => {
    const newName = `Area ${layerIndex + 1}`;
    layer.options.className = newName;
    
    let layerType: 'circle' | 'rectangle' | 'polygon' = 'polygon';
    if (layer._mRadius) {
      layerType = 'circle';
    } else if (layer instanceof L.Rectangle) {
      layerType = 'rectangle';
    } else if (layer._latlngs) {
      layerType = 'polygon';
    }
    
    const area: IGeographical = {
      id: layerIndex,
      name: newName,
      color: layer.options.color,
      type: layerType,
      area: layer._latlngs ?? undefined,
      point: layer._latlng
        ? ([layer._latlng.lat, layer._latlng.lng] as LatLngExpression)
        : undefined,
      radius: layer._mRadius ?? undefined,
      initialDate: undefined,
      endDate: undefined,
    };
    
    areas.push(area);
    layerIndex++;
  });
  
  onChange(areas);
}

function updateDraw({
  event,
  created = false,
  value,
  onChange,
  valueDrawOpenMoment,
  setValueDrawMomentOpen,
  limit,
  setEnableDraw,
  enableDraw,
  lastOnChangeValueRef,
}: {
  event: any;
  created?: boolean;
  value: IGeographical[];
  onChange: (value: IGeographical[]) => void;
  valueDrawOpenMoment: IGeographical[];
  setValueDrawMomentOpen: Dispatch<SetStateAction<IGeographical[]>>;
  limit?: number;
  setEnableDraw: Dispatch<SetStateAction<boolean>>;
  enableDraw: boolean;
  lastOnChangeValueRef: React.MutableRefObject<string>;
}) {
  if (created) {
    const colorGenerate =
      '#' +
      Math.floor(Math.random() * 0x1000000)
        .toString(16)
        .padStart(6, '0');

    const nextIndex = valueDrawOpenMoment.length + 1;

    if (event.layer) {
      event.layer.options.color = colorGenerate;
      event.layer.options.className = `Area ${nextIndex}`;
    }
  }

  let layers: IGeographical[] = [];

  if (created && event.layer) {
    let layerType: 'circle' | 'rectangle' | 'polygon' = 'polygon';
    if (event.layer._mRadius) {
      layerType = 'circle';
    } else if (event.layer instanceof L.Rectangle) {
      layerType = 'rectangle';
    } else if (event.layer._latlngs) {
      layerType = 'polygon';
    }

    const newLayer: IGeographical = {
      id: Date.now(),
      name: event.layer.options.className || `Area ${valueDrawOpenMoment.length + 1}`,
      color: event.layer.options.color,
      type: layerType,
      area: event.layer._latlngs ?? undefined,
      point: event.layer._latlng
        ? ([event.layer._latlng.lat, event.layer._latlng.lng] as LatLngExpression)
        : undefined,
      radius: event.layer._mRadius ?? undefined,
      initialDate: undefined,
      endDate: undefined,
    };
    layers = [...valueDrawOpenMoment, newLayer];
  } else {
    layers = Object.keys(event.target.options.renderer._layers).map((layerDraw) => {
      const currentLayer = event.target.options.renderer._layers[layerDraw];
      const layerOld =
        valueDrawOpenMoment.filter((layer) => {
          return currentLayer.options.className == layer?.name;
        })[0] ??
        value.filter((layer) => {
          return currentLayer.options.className == layer?.name;
        })[0];

      let type: 'circle' | 'rectangle' | 'polygon' = 'polygon';
      if (currentLayer._mRadius !== undefined) {
        type = 'circle';
      } else if (currentLayer._latlngs && currentLayer._latlngs[0] && currentLayer._latlngs[0].length === 4) {
        type = 'rectangle';
      }

      return {
        id: Number(layerDraw),
        name: layerOld
          ? layerOld.name
          : `Área ${currentLayer.options.color}`,
        color: currentLayer.options.color,
        type: type,
        area: currentLayer._latlngs ?? undefined,
        point: currentLayer._latlng
          ? ([
            currentLayer._latlng.lat,
            currentLayer._latlng.lng,
          ] as LatLngExpression)
          : undefined,
        radius: currentLayer._mRadius ?? undefined,
        initialDate: layerOld ? layerOld.initialDate : undefined,
        endDate: layerOld ? layerOld.endDate : undefined,
      };
    });
  }

  const filteredLayers = layers.filter(
    (layer) => layer.radius || (layer.area && layer.area[0] && layer.area[0]?.length >= 3)
  );

  const newEnableDraw = limit ? filteredLayers.length < limit : true;
  const filteredLayersString = JSON.stringify(filteredLayers);

  if (newEnableDraw !== enableDraw) {
    setEnableDraw(newEnableDraw);
  }

  if (filteredLayersString !== lastOnChangeValueRef.current) {
    lastOnChangeValueRef.current = filteredLayersString;
    onChange(filteredLayers);
  }

  const currentValueString = JSON.stringify(valueDrawOpenMoment);
  if (filteredLayersString !== currentValueString) {
    setValueDrawMomentOpen(filteredLayers);
  }

  return event;
}

function changeZIndexOverlay({ map, zIndex }: Readonly<{ map: Map; zIndex: string }>) {
  const overlayPane = map.getPane('overlayPane');
  if (overlayPane) {
    overlayPane.style.zIndex = zIndex;
  }
}